/**
 * AdminDevicesPage — manage Android tablet display devices
 *
 * Admins create device records here (with PIN), then hand the
 * Device ID + PIN to staff who activate the physical tablet.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Tablet, Plus, Edit2, Trash2, RefreshCw,
  Wifi, WifiOff, AlertTriangle, Copy, Eye, EyeOff,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { devicesAPI, gatesAPI, announcementZonesAPI } from '../../services/api';
import PageHeader from '../../components/UI/PageHeader';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import StatusBadge from '../../components/UI/StatusBadge';
import EmptyState from '../../components/UI/EmptyState';

// ── Helpers ──────────────────────────────────────────────────────────────────

function timeAgo(dateStr) {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 2) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function isOnline(lastSeen) {
  if (!lastSeen) return false;
  return Date.now() - new Date(lastSeen).getTime() < 90000; // 90s
}

// ── Device Form Modal ─────────────────────────────────────────────────────────

function DeviceFormModal({ device, gates, zones, onSave, onClose }) {
  const isEdit = !!device;
  const [form, setForm] = useState({
    device_id: device?.device_id || '',
    device_name: device?.device_name || '',
    pin: '',
    device_type: device?.device_type || 'gate',
    gate_id: device?.gate_id || '',
    zone_code: device?.zone_code || '',
    notes: device?.notes || '',
    is_active: device?.is_active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showPin, setShowPin] = useState(false);

  function set(key, val) {
    setForm(prev => ({ ...prev, [key]: val }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = { ...form };
      if (!payload.pin && isEdit) delete payload.pin; // don't update PIN if empty
      if (payload.device_type === 'gate') payload.zone_code = null;
      if (payload.device_type === 'zone') payload.gate_id = null;

      if (isEdit) {
        await devicesAPI.update(device.id, payload);
        toast.success(payload.pin
          ? 'PIN updated — the tablet has been signed out and must sign in with the new PIN'
          : 'Device updated');
      } else {
        await devicesAPI.create(payload);
        toast.success('Device created');
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save device');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-y-auto max-h-screen">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEdit ? 'Edit Device' : 'Add New Device'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Device ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Device ID *</label>
            <input
              value={form.device_id}
              onChange={e => set('device_id', e.target.value.toUpperCase())}
              disabled={isEdit}
              placeholder="e.g. GATE-A12-001"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            />
            <p className="text-xs text-gray-500 mt-1">Unique identifier entered on the tablet</p>
          </div>

          {/* Device Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Device Name *</label>
            <input
              value={form.device_name}
              onChange={e => set('device_name', e.target.value)}
              placeholder="e.g. Gate A12 Tablet"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* PIN */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              PIN {isEdit ? '(leave blank to keep current)' : '*'}
            </label>
            <div className="relative">
              <input
                type={showPin ? 'text' : 'password'}
                inputMode="numeric"
                value={form.pin}
                onChange={e => set('pin', e.target.value.replace(/\D/g, ''))}
                placeholder="4–8 digits"
                maxLength={8}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={() => setShowPin(s => !s)}
                className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
              >
                {showPin ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {/* Device Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To *</label>
            <div className="flex gap-3">
              {['gate', 'zone'].map(t => (
                <button
                  key={t}
                  type="button"
                  onClick={() => set('device_type', t)}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    form.device_type === t
                      ? 'bg-blue-600 border-blue-600 text-white'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {t === 'gate' ? 'Gate' : 'Zone'}
                </button>
              ))}
            </div>
          </div>

          {/* Gate selector */}
          {form.device_type === 'gate' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Gate *</label>
              <select
                value={form.gate_id}
                onChange={e => set('gate_id', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select gate…</option>
                {gates.map(g => (
                  <option key={g.id} value={g.id}>
                    {g.gate_code}{g.terminal ? ` — Terminal ${g.terminal}` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Zone selector */}
          {form.device_type === 'zone' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Zone *</label>
              <select
                value={form.zone_code}
                onChange={e => set('zone_code', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select zone…</option>
                {zones.map(z => (
                  <option key={z.id} value={z.zone_code}>{z.zone_code}{z.zone_name ? ` — ${z.zone_name}` : ''}</option>
                ))}
              </select>
              {form.zone_code === 'ALL' && (
                <p className="text-xs text-gray-500 mt-1">
                  Plays every zone announcement (airport-wide, all terminals, belts, weather). Gate boarding
                  programmes play only on that gate's devices — assign a gate for those.
                </p>
              )}
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
              rows={2}
              placeholder="Optional notes…"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          {/* Active toggle (edit only) */}
          {isEdit && (
            <label className="flex items-center gap-3 cursor-pointer">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={e => set('is_active', e.target.checked)}
                  className="sr-only"
                />
                <div className={`w-11 h-6 rounded-full transition-colors ${form.is_active ? 'bg-blue-600' : 'bg-gray-300'}`} />
                <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.is_active ? 'translate-x-5' : ''}`} />
              </div>
              <span className="text-sm font-medium text-gray-700">Device active</span>
            </label>
          )}

          {error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">
              <AlertTriangle size={14} />
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="ghost" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={saving} className="flex-1">
              {isEdit ? 'Save Changes' : 'Create Device'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Device Row ────────────────────────────────────────────────────────────────

function DeviceRow({ device, onEdit, onRevoke, onDelete, onCopyId }) {
  const online = isOnline(device.last_seen);

  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50">
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${online ? 'bg-green-500' : 'bg-gray-300'}`} />
          <div>
            <div className="text-sm font-semibold text-gray-900 font-mono">{device.device_id}</div>
            <div className="text-xs text-gray-500">{device.device_name}</div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <StatusBadge
          label={device.device_type === 'gate'
            ? (device.gate?.gate_code || `Gate ${device.gate_id}`)
            : (device.zone_code || 'Zone')}
          tone={device.device_type === 'gate' ? 'info' : 'purple'}
        />
      </td>
      <td className="px-4 py-3">
        <StatusBadge
          label={device.is_activated ? 'Activated' : 'Pending'}
          tone={device.is_activated ? 'success' : 'yellow'}
        />
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1 text-xs">
          {online
            ? <><Wifi size={11} className="text-green-500" /><span className="text-green-600">Online</span></>
            : <><WifiOff size={11} className="text-gray-400" /><span className="text-gray-400">{timeAgo(device.last_seen)}</span></>
          }
        </div>
      </td>
      <td className="px-4 py-3">
        {!device.is_active && <StatusBadge label="Disabled" tone="danger" />}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2 justify-end">
          <button
            onClick={() => onCopyId(device.device_id)}
            title="Copy Device ID"
            className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
          >
            <Copy size={14} />
          </button>
          <button
            onClick={() => onEdit(device)}
            title="Edit"
            className="p-1.5 text-blue-500 hover:text-blue-700 rounded"
          >
            <Edit2 size={14} />
          </button>
          {device.is_activated && (
            <button
              onClick={() => onRevoke(device)}
              title="Revoke token (force re-activation)"
              className="p-1.5 text-amber-500 hover:text-amber-700 rounded"
            >
              <RefreshCw size={14} />
            </button>
          )}
          <button
            onClick={() => onDelete(device)}
            title="Delete"
            className="p-1.5 text-red-400 hover:text-red-600 rounded"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </td>
    </tr>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function AdminDevicesPage() {
  const [devices, setDevices] = useState([]);
  const [gates, setGates] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalDevice, setModalDevice] = useState(undefined); // undefined=closed, null=new, obj=edit

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dRes, gRes, zRes] = await Promise.all([
        devicesAPI.getAll(),
        gatesAPI.getAll(),
        announcementZonesAPI.getAll(),
      ]);
      setDevices(dRes.data.data || []);
      setGates(gRes.data.data || []);
      setZones(zRes.data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh online status every 30s
  useEffect(() => {
    const id = setInterval(() => setDevices(prev => [...prev]), 30000);
    return () => clearInterval(id);
  }, []);

  async function handleRevoke(device) {
    if (!confirm(`Revoke token for ${device.device_id}? The tablet will need to re-activate.`)) return;
    try {
      await devicesAPI.revoke(device.id);
      load();
    } catch { toast.error('Failed to revoke token'); }
  }

  async function handleDelete(device) {
    if (!confirm(`Delete device ${device.device_id}? This cannot be undone.`)) return;
    try {
      await devicesAPI.delete(device.id);
      load();
    } catch { toast.error('Failed to delete device'); }
  }

  function handleCopyId(deviceId) {
    navigator.clipboard.writeText(deviceId).then(() => {
      toast.success(`Copied: ${deviceId}`);
    });
  }

  const onlineCount = devices.filter(d => isOnline(d.last_seen)).length;
  const activatedCount = devices.filter(d => d.is_activated).length;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <PageHeader
        icon={Tablet}
        title="Display Devices"
        subtitle="Android tablets registered as PA playback devices"
        actions={
          <>
            <Button variant="ghost" size="sm" icon={RefreshCw} onClick={load} />
            <Button variant="primary" icon={Plus} onClick={() => setModalDevice(null)}>Add Device</Button>
          </>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label: 'Total Devices', value: devices.length, color: 'text-gray-900' },
          { label: 'Activated', value: activatedCount, color: 'text-blue-600' },
          { label: 'Online Now', value: onlineCount, color: 'text-green-600' },
        ].map(s => (
          <Card key={s.label} className="text-center">
            <div className={`text-3xl font-bold ${s.color}`}>{s.value}</div>
            <div className="text-xs text-gray-500 mt-1">{s.label}</div>
          </Card>
        ))}
      </div>

      {/* Setup instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 text-sm text-blue-800">
        <strong>How to set up a tablet:</strong>
        <ol className="mt-1 ml-4 list-decimal space-y-0.5 text-blue-700">
          <li>Create a device record below with a Device ID and PIN</li>
          <li>On the Android tablet, open Chrome and go to <code className="bg-blue-100 px-1 rounded">/device-setup</code></li>
          <li>Enter the Device ID and PIN — the tablet activates automatically</li>
          <li>Install as PWA: Chrome menu → "Add to Home screen"</li>
        </ol>
      </div>

      {/* Table */}
      {loading ? (
        <Card padded={false} className="overflow-hidden">
          <div className="p-10 text-center text-gray-400 text-sm">Loading…</div>
        </Card>
      ) : devices.length === 0 ? (
        <EmptyState
          icon={Tablet}
          title="No devices yet"
          subtitle="Add a device to get started"
          actionLabel="Add Device"
          actionIcon={Plus}
          onAction={() => setModalDevice(null)}
        />
      ) : (
        <Card padded={false} className="overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Device', 'Assigned To', 'Status', 'Last Seen', '', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {devices.map(d => (
                <DeviceRow
                  key={d.id}
                  device={d}
                  onEdit={setModalDevice}
                  onRevoke={handleRevoke}
                  onDelete={handleDelete}
                  onCopyId={handleCopyId}
                />
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Modal */}
      {modalDevice !== undefined && (
        <DeviceFormModal
          device={modalDevice}
          gates={gates}
          zones={zones}
          onSave={() => { setModalDevice(undefined); load(); }}
          onClose={() => setModalDevice(undefined)}
        />
      )}
    </div>
  );
}
